import React, { useCallback } from 'react';
import { createFormField } from 'Shared/utils/createFormField';
import SelectUi from 'react-select';
import { WpIcon } from 'Wpds/components/icon';

const defaultCustomStyles = {
  control: base => ({ ...base, fontSize: '14px' }),
  menuList: base => ({ ...base, fontSize: '14px' }),
};

function getValue(value, options = [], isMulti) {
  if (!value) return undefined;
  if (isMulti && Array.isArray(value)) return options.filter(option => value.some(item => item.value === option.value));
  return options.find(option => option.value === value);
}

function getOutputValue(fieldValue, isMulti) {
  if (!fieldValue) return undefined;
  if (isMulti && Array.isArray(fieldValue)) return fieldValue.map(item => item.value);
  return fieldValue.value;
}

type Props = {
  name?: string;
  value: string;
  options: Array<{
    value: string;
    label: string;
  }>;
  autoFocus?: boolean;
  onChange: Function;
  onInputChange?: Function;
  customGetOutputValue?: Function;
  menuShouldBlockScroll?: boolean;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  customStyles?: {
    control?: Function;
    menuList?: Function;
    menuPortal?: Function;
  };
  isMulti?: boolean;
  isClearable?: boolean;
  components?;
  onFocus?: Function;
  onBlur?: Function;
  isLoading?: boolean;
  blurInputOnSelect?: boolean;
  inputValue?: string;
  captureMenuScroll?: boolean;
  hideSelectedOptions?: boolean;
  noOptionsMessage?: React.ReactNode | null;
  menuPortalTarget?: React.ReactNode;
  inputId?: string;
};

export function Select({
  inputId,
  value: selectValue,
  options,
  onChange,
  placeholder,
  customGetOutputValue,
  onInputChange,
  isClearable = false,
  disabled,
  className = '',
  customStyles = defaultCustomStyles,
  isMulti = false,
  autoFocus = false,
  components,
  menuPortalTarget,
  menuShouldBlockScroll,
  onFocus,
  onBlur,
  isLoading,
  blurInputOnSelect,
  inputValue,
  captureMenuScroll,
  hideSelectedOptions,
  noOptionsMessage,
}: Props) {
  const value = typeof selectValue === 'object' ? selectValue : getValue(selectValue, options, isMulti);

  const handleChange = useCallback(
    fieldValue => {
      onChange(customGetOutputValue ? customGetOutputValue(fieldValue) : getOutputValue(fieldValue, isMulti));
    },
    [onChange, customGetOutputValue, isMulti]
  );

  return (
    <SelectUi
      inputId={inputId}
      className={`Select ${className}`}
      value={value}
      autoFocus={autoFocus}
      options={options}
      isDisabled={disabled}
      placeholder={placeholder}
      components={{
        DropdownIndicator: () => (
          <span style={{ padding: '5px 10px 10px' }}>
            <WpIcon name="chevron-down" />
          </span>
        ),
        IndicatorSeparator: null,
        ...components,
      }}
      inputValue={inputValue}
      onFocus={onFocus}
      onBlur={onBlur}
      styles={customStyles}
      menuShouldBlockScroll={menuShouldBlockScroll}
      onChange={handleChange}
      onInputChange={onInputChange}
      menuPortalTarget={menuPortalTarget}
      isMulti={isMulti}
      isLoading={isLoading}
      isClearable={isClearable}
      blurInputOnSelect={blurInputOnSelect}
      captureMenuScroll={captureMenuScroll}
      noOptionsMessage={noOptionsMessage}
      hideSelectedOptions={hideSelectedOptions}
    />
  );
}

export default createFormField(Select);
